// ---------------------------------------------------------------------------
// SINGLE SOURCE OF TRUTH for all editable landing copy + images.
// The CRM mini-CMS stores only *overrides*; these defaults are deep-merged
// on top at runtime (see ContentContext). Editing here changes the fallback.
// ---------------------------------------------------------------------------

const defaultContent = {
  header: {
    brand: "Syntix",
    nav: [
      { label: "Lavori", href: "#lavori" },
      { label: "Processo", href: "#processo" },
      { label: "Investimento", href: "#investimento" },
      { label: "FAQ", href: "#faq" },
    ],
    cta: "Raccontami la tua azienda",
  },

  hero: {
    eyebrow: "Software gestionale su misura",
    headline: "Il tuo gestionale dovrebbe adattarsi alla tua azienda.",
    headline_accent: "Non il contrario.",
    subheadline:
      "Progetto CRM, gestionali e applicazioni business su misura per aziende che hanno superato Excel, software scollegati e procedure manuali.",
    techline: "React · FastAPI · MongoDB · API · AI",
    cta_primary: "Raccontami come lavora la tua azienda",
    cta_secondary: "Guarda cosa ho costruito",
    note: "Analizzo personalmente ogni progetto.",
  },

  problem: {
    eyebrow: "Il problema",
    headline:
      "Se servono cinque strumenti per fare un solo lavoro, il problema non sono le persone.",
    intro:
      "Oggi, un unico processo aziendale è spesso spezzato in strumenti che non si parlano:",
    tools: [
      { task: "Clienti", tool: "CRM" },
      { task: "Preventivi", tool: "Word" },
      { task: "Ordini", tool: "Excel" },
      { task: "Documenti", tool: "Drive" },
      { task: "Comunicazioni", tool: "WhatsApp" },
      { task: "Pagamenti", tool: "altro software" },
      { task: "Report", tool: "fogli manuali" },
    ],
    closing:
      "Quando gli strumenti iniziano a rallentare il lavoro invece di semplificarlo, può essere il momento di costruire qualcosa intorno al modo reale in cui lavora l'azienda.",
    tagline: "Prima del codice viene il processo.",
  },

  centralize: {
    eyebrow: "Un unico sistema",
    title: "Un sistema. I processi che servono davvero alla tua azienda.",
    intro:
      "Non tutto insieme, non tutto subito: solo i moduli che il tuo processo richiede davvero. Ecco le aree che si possono centralizzare in un'unica piattaforma.",
    areas: [
      "Clienti",
      "CRM",
      "Pipeline commerciale",
      "Ordini",
      "Preventivi",
      "Contratti",
      "Documenti",
      "Pagamenti",
      "Prenotazioni",
      "Dipendenti",
      "Ruoli e permessi",
      "Notifiche",
      "Dashboard",
      "Report",
      "Automazioni",
      "AI",
      "API",
      "Servizi esterni",
    ],
    note_bold: "Non è un pacchetto standard.",
    note: "Ogni sistema viene progettato in base ai processi del progetto.",
  },

  diagnostic: {
    eyebrow: "Diagnosi",
    title: "Raccontami una giornata normale nella tua azienda.",
    questions: [
      "Come entra un nuovo cliente?",
      "Dove viene registrato?",
      "Come viene creato un preventivo?",
      "Come viene assegnata un'attività?",
      "Dove vengono archiviati i documenti?",
      "Quante volte viene inserito lo stesso dato?",
      "Quali attività vengono fatte manualmente ogni giorno?",
      "Quali software non comunicano tra loro?",
    ],
    closing: "Partiamo da lì.",
    cta: "Analizziamo il processo",
  },

  standard_vs_custom: {
    eyebrow: "Standard vs custom",
    title: "Quando ha senso il software su misura — e quando no.",
    intro:
      "I software standard sono un'ottima scelta in molti casi. Il custom ha senso solo quando serve davvero.",
    standard_badge: "Software standard",
    standard_title: "Ottimo quando il processo è comune",
    standard_body:
      "Quando il processo aziendale è comune e un prodotto esistente risolve già bene il problema, il software standard è la scelta più rapida ed economica. Perfetto per esigenze diffuse e ben coperte dal mercato.",
    standard_note: "Meno flessibile sui processi specifici",
    custom_badge: "Software custom",
    custom_title: "Ha senso quando:",
    custom_when: [
      "I processi sono specifici",
      "Il software esistente limita l'azienda",
      "Devono comunicare più sistemi",
      "Esistono workflow proprietari",
      "Il processo è un vantaggio competitivo",
      "Serve costruire un nuovo prodotto digitale",
    ],
    closing: "Non costruisco software custom quando non serve.",
  },

  process: {
    eyebrow: "Come lavoro",
    title: "Un metodo in sette fasi, dalla prima analisi all'evoluzione.",
    intro:
      "Prima del codice viene il processo. Ogni progetto segue lo stesso percorso strutturato.",
    steps: [
      { n: "01", title: "Analisi", body: "Processi, utenti, problemi, obiettivi." },
      { n: "02", title: "Architettura", body: "Database, backend, API, ruoli, sicurezza." },
      { n: "03", title: "UX", body: "Interfacce e workflow." },
      { n: "04", title: "Sviluppo", body: "Frontend, backend e integrazioni." },
      { n: "05", title: "Testing", body: "Verifica sui processi reali." },
      { n: "06", title: "Deploy", body: "Messa online e infrastruttura." },
      { n: "07", title: "Evoluzione", body: "Nuove funzionalità e supporto." },
    ],
  },

  investment: {
    eyebrow: "Investimento",
    title: "Software pensato per risolvere problemi strutturali.",
    intro:
      "Indicazioni di massima per capire l'ordine di grandezza. Non un listino: ogni progetto viene quotato dopo l'analisi.",
    tiers: [
      {
        title: "CRM e sistemi custom",
        price: "da €7.500",
        body: "Gestione clienti, pipeline e workflow su misura per un processo specifico.",
        highlighted: false,
      },
      {
        title: "Gestionali e applicazioni business",
        price: "da €12.000 / €15.000",
        body: "Più moduli che comunicano tra loro, ruoli, automazioni e integrazioni.",
        highlighted: true,
      },
      {
        title: "SaaS e piattaforme complesse",
        price: "preventivo su analisi",
        body: "Prodotti digitali multi-utente, architetture scalabili e infrastruttura dedicata.",
        highlighted: false,
      },
    ],
    note: "Il costo finale dipende da architettura, workflow, utenti, integrazioni, infrastruttura e complessità. Il modo migliore per avere un numero è partire dall'analisi del processo.",
    cta: "Richiedi un'analisi",
  },

  about: {
    eyebrow: "Chi progetta e costruisce",
    title: "Parli direttamente con chi costruisce il sistema.",
    p1: "Syntix — Software Developer & Software Architect. Circa 16 anni di esperienza professionale nel digitale e founder di una piattaforma SaaS proprietaria.",
    p2: "Nessun intermediario: analizzo personalmente ogni progetto e sviluppo in prima persona architettura, backend, frontend, API, database, automazioni, AI e infrastruttura.",
    builds: ["Architettura", "Backend", "Frontend", "API", "Database", "Automazioni", "AI", "Infrastruttura"],
    name: "Syntix",
    role: "Software Developer & Architect",
    years: "~16",
    years_label: "anni nel digitale",
    saas: "1",
    saas_label: "SaaS proprietaria",
  },

  faq: {
    eyebrow: "FAQ",
    title: "Domande frequenti",
    items: [
      { q: "Quanto costa un gestionale su misura?", a: "Dipende da moduli, utenti, integrazioni e complessità. Come ordine di grandezza: CRM e sistemi custom da €7.500, gestionali e applicazioni business da €12.000–€15.000, SaaS e piattaforme complesse su preventivo dopo l'analisi." },
      { q: "Quanto tempo richiede lo sviluppo?", a: "Varia con la portata del progetto. Si parte quasi sempre da una prima versione utilizzabile e si aggiungono moduli in fasi successive, così da mettere in produzione valore il prima possibile." },
      { q: "Il codice può diventare proprietà del cliente?", a: "Sì. La titolarità del codice può essere trasferita al cliente: le condizioni vengono definite insieme in fase di proposta." },
      { q: "È possibile importare dati da sistemi esistenti?", a: "Sì. È possibile importare dati da Excel, gestionali o database esistenti, con un processo di migrazione e bonifica dei dati." },
      { q: "Può integrarsi con il software già utilizzato?", a: "Quando i sistemi espongono API o formati di scambio, sì: l'obiettivo è far comunicare gli strumenti già in uso invece di sostituirli tutti." },
      { q: "Possiamo partire con una prima versione e aggiungere moduli dopo?", a: "Sì, è l'approccio consigliato: si parte da un nucleo solido e si evolve per fasi, in base alle priorità reali dell'azienda." },
      { q: "È possibile gestire più sedi?", a: "Sì. L'architettura può supportare più sedi, con dati separati o condivisi a seconda delle necessità." },
      { q: "È possibile creare ruoli e permessi differenti?", a: "Sì. Gli accessi basati su ruoli sono parte standard dell'architettura: ogni utente vede e fa solo ciò che gli compete." },
      { q: "Si possono integrare Stripe e PayPal?", a: "Sì. È possibile integrare i principali gateway di pagamento, tra cui Stripe e PayPal, per pagamenti e abbonamenti." },
      { q: "È possibile integrare AI?", a: "Sì. È possibile integrare funzionalità di AI dove portano valore reale: assistenza, analisi, automazione di attività ripetitive." },
      { q: "Dove viene ospitata l'applicazione?", a: "Su infrastruttura cloud dedicata, scelta in base a requisiti di performance, sicurezza e budget. L'hosting viene definito in fase di architettura." },
      { q: "Come viene gestito il supporto?", a: "Dopo il rilascio il progetto continua a evolvere: correzioni, nuove funzionalità e supporto vengono gestiti in modo continuativo." },
    ],
  },

  final_cta: {
    headline: "Non raccontarmi quale software vuoi.",
    headline_accent: "Raccontami cosa vuoi smettere di fare a mano.",
    body: "Descrivi come lavora oggi la tua azienda, quali strumenti utilizzi e dove il processo inizia a diventare inefficiente.",
    cta: "Raccontami il progetto",
  },

  contact: {
    eyebrow: "Il tuo progetto",
    heading: "Raccontami come lavora la tua azienda",
    subheading:
      "Più mi racconti del processo reale, più la mia risposta sarà utile. Se ha senso, ti rispondo con domande mirate e una proposta di percorso.",
    benefits: [
      "Analizzo personalmente ogni progetto.",
      "Prima del codice viene il processo.",
      "Il software si adatta all'azienda, non il contrario.",
    ],
    privacy_note: "I tuoi dati vengono usati solo per ricontattarti su questo progetto.",
    success_title: "Ricevuto. Grazie.",
    success_body:
      "Ti rispondo personalmente entro 1–2 giorni lavorativi con le prossime domande utili a inquadrare il progetto.",
    submit_cta: "Invia il progetto",
    response_note: "Rispondo personalmente entro 1–2 giorni lavorativi.",
  },

  footer: {
    brand: "Syntix",
    bio: "Software Developer & Software Architect. Analizzo processi aziendali complessi e costruisco il software necessario per gestirli.",
    techline: "React · FastAPI · MongoDB · API · AI",
    copyright: "Syntix. Tutti i diritti riservati.",
  },

  privacy: {
    title: "Privacy Policy",
    updated: "18 agosto 2026",
    intro:
      "La presente informativa descrive le modalità di trattamento dei dati personali degli utenti che consultano il sito e inviano richieste tramite il form di contatto, ai sensi del Regolamento (UE) 2016/679 (GDPR).",
    blocks: [
      {
        heading: "1. Titolare del trattamento",
        body: "Titolare: [RAGIONE SOCIALE / NOME] — P.IVA [P.IVA], con sede in [INDIRIZZO / CITTÀ]. Email di contatto: info@syntix.it.",
      },
      {
        heading: "2. Dati raccolti",
        body: "Tramite il form raccogliamo: nome, cognome, azienda, email, telefono (facoltativo), settore, numero di dipendenti/utenti, descrizione del processo, problema principale, obiettivi di automazione, budget e tempistiche. Vengono inoltre raccolti dati di navigazione (es. cookie tecnici e, previo consenso, di analisi).",
      },
      {
        heading: "3. Finalità e base giuridica",
        body: "I dati sono trattati per rispondere alle richieste e valutare una possibile collaborazione (base giuridica: misure precontrattuali su richiesta dell'interessato) e, con consenso, per finalità di analisi statistica del sito.",
      },
      {
        heading: "4. Conservazione",
        body: "I dati sono conservati per il tempo necessario a gestire la richiesta e per gli adempimenti di legge, e comunque non oltre [PERIODO DI CONSERVAZIONE].",
      },
      {
        heading: "5. Diritti dell'interessato",
        body: "L'utente può esercitare i diritti di accesso, rettifica, cancellazione, limitazione, opposizione e portabilità scrivendo a info@syntix.it. È inoltre possibile proporre reclamo al Garante per la protezione dei dati personali.",
      },
      {
        heading: "6. Comunicazione dei dati",
        body: "I dati possono essere trattati da fornitori di servizi tecnici (hosting, email, analisi) nominati responsabili del trattamento. Non è previsto trasferimento dei dati al di fuori dell'area consentita senza adeguate garanzie.",
      },
    ],
  },

  termini: {
    title: "Termini e Condizioni",
    updated: "18 agosto 2026",
    intro: "",
    blocks: [
      {
        heading: "1. Oggetto",
        body: "I presenti termini regolano l'utilizzo del sito e la richiesta di servizi di analisi, progettazione e sviluppo di software su misura offerti da [RAGIONE SOCIALE / NOME].",
      },
      {
        heading: "2. Natura delle informazioni",
        body: "Le indicazioni economiche presenti sul sito sono di massima e non costituiscono offerta contrattuale. Ogni progetto viene quotato dopo un'analisi del processo e formalizzato con proposta e contratto dedicati.",
      },
      {
        heading: "3. Proposta e contratto",
        body: "Tempi, costi, ambito, titolarità del codice, garanzie e supporto vengono definiti nel contratto specifico di progetto. In assenza di contratto, nessun impegno si intende assunto.",
      },
      {
        heading: "4. Proprietà intellettuale",
        body: "Contenuti, marchi e materiali del sito appartengono ai rispettivi titolari. La titolarità del software sviluppato è disciplinata dal contratto di progetto.",
      },
      {
        heading: "5. Limitazione di responsabilità",
        body: "Il sito è fornito “così com'è”. Nei limiti di legge, si esclude ogni responsabilità per danni derivanti dall'uso delle informazioni pubblicate a scopo divulgativo.",
      },
      {
        heading: "6. Legge applicabile e foro",
        body: "I presenti termini sono regolati dalla legge italiana. Foro competente: [FORO COMPETENTE].",
      },
    ],
  },

  cookie: {
    title: "Cookie Policy",
    updated: "18 agosto 2026",
    intro: "",
    blocks: [
      {
        heading: "1. Cosa sono i cookie",
        body: "I cookie sono piccoli file di testo che i siti salvano sul dispositivo dell'utente per funzionare correttamente o per raccogliere informazioni statistiche.",
      },
      {
        heading: "2. Cookie tecnici",
        body: "Utilizziamo cookie e memoria locale strettamente necessari al funzionamento del sito (es. preferenze di accessibilità e stato del consenso). Non richiedono consenso.",
      },
      {
        heading: "3. Cookie di analisi (Google Analytics)",
        body: "Previo consenso, utilizziamo Google Analytics (GA4) per analizzare in forma aggregata l'utilizzo del sito, con IP anonimizzato. Questi cookie vengono installati solo dopo aver cliccato “Accetta” nel banner. ID di misurazione: [GA MEASUREMENT ID].",
      },
      {
        heading: "4. Gestione del consenso",
        body: "È possibile modificare la scelta in qualsiasi momento cancellando i dati del sito dal proprio browser: alla visita successiva il banner verrà mostrato nuovamente. In assenza di consenso non viene installato alcun cookie di analisi.",
      },
      {
        heading: "5. Titolare e contatti",
        body: "Titolare: [RAGIONE SOCIALE / NOME] — Email: info@syntix.it.",
      },
    ],
  },

  images: {
    diagnostic:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80",
    about:
      "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1200&q=80",
  },

  seo: {
    title: "Syntix — Software gestionale e CRM su misura per aziende",
    description:
      "Progetto CRM, gestionali e applicazioni business su misura per PMI che hanno superato Excel, software scollegati e procedure manuali.",
    keywords:
      "software gestionale su misura, CRM custom, sviluppo software aziendale, gestionale personalizzato, applicazioni business, automazione processi, software custom PMI",
    canonical_base: "https://syntix.it",
    og_image: "",
    gsc_verification: "",
    author: "Syntix",
    job_title: "Software Developer & Software Architect",
  },
};

export default defaultContent;
